import { Preferences } from '@capacitor/preferences'
import { isNativeApp } from '@/utils/native-platform'
import { reportError } from '@/utils/error-reporter'

/**
 * More reliable key/value storage: localStorage in the browser, native storage
 * (using @capacitor/preferences) in the native app.
 */

const nativeCache = new Map<string, string>()

function reportWriteFailure(operation: 'set' | 'remove', key: string) {
  return (error: unknown) =>
    reportError(error, {
      context: 'persistent-storage',
      metadata: { section: 'persistentStorage', values: { operation, key } }
    })
}

async function loadPersistentStorage() {
  if (!isNativeApp()) {
    return
  }

  try {
    const { keys } = await Preferences.keys()
    const entries = await Promise.all(
      keys.map(
        async (key) => [key, (await Preferences.get({ key })).value] as const
      )
    )
    for (const [key, value] of entries) {
      if (value !== null) {
        nativeCache.set(key, value)
      }
    }
  } catch (error) {
    reportError(error, {
      context: 'persistent-storage',
      metadata: { section: 'persistentStorage', values: { operation: 'load' } }
    })
  }
}

const persistentStorage = {
  getItem(key: string): string | null {
    if (isNativeApp()) {
      return nativeCache.get(key) ?? null
    }

    return localStorage.getItem(key)
  },

  setItem(key: string, value: string) {
    if (isNativeApp()) {
      nativeCache.set(key, value)
      Preferences.set({ key, value }).catch(reportWriteFailure('set', key))
      return
    }

    localStorage.setItem(key, value)
  },

  removeItem(key: string) {
    if (isNativeApp()) {
      nativeCache.delete(key)
      Preferences.remove({ key }).catch(reportWriteFailure('remove', key))
      return
    }

    localStorage.removeItem(key)
  }
}

export { loadPersistentStorage, persistentStorage }
